import type { Database, Json } from "@/lib/database.types";
import type { PqrCategory, PqrStatus } from "@/features/pqr/pqr.types";
import type { SupportedLocale } from "@/types/auth";
import type { SubscriptionTier } from "@/types/projects";

export const adminUserPageSize = 50;
export const adminLogPageSize = 50;
export const adminBanDurations = [
  "24_hours",
  "3_days",
  "7_days",
  "30_days",
  "permanent",
] as const;
export const adminLogSeverities = ["info", "warning", "error", "critical"] as const;

export type AdminUserStatusFilter = "all" | "active" | "banned" | "pending_deletion" | "deleted";
export type AdminTierFilter = "all" | SubscriptionTier;
export type AdminBanDuration = (typeof adminBanDurations)[number];
export type AdminLogSeverity = (typeof adminLogSeverities)[number];
export type AdminPqrStatus = PqrStatus;
export type AdminPqrCategory = PqrCategory;
export type AdminLogRecord = Database["public"]["Tables"]["app_logs"]["Row"];
export type AdminAiAbuseReviewStatus = "acknowledged" | "cleared" | "open";
export type AdminQuotaResourceKind =
  | "database_bandwidth"
  | "edge_invocations"
  | "monthly_ai_budget"
  | "storage";

export interface AdminUsersFilters {
  page: number;
  searchText: string;
  statusFilter: AdminUserStatusFilter;
  tierFilter: AdminTierFilter;
}

export interface AdminUserSummary {
  billingStatus: string;
  creditBalance: number;
  deletedAt: string | null;
  deletionScheduledAt: string | null;
  email: string;
  lastLoginAt: string | null;
  name: string;
  registeredAt: string;
  status: Exclude<AdminUserStatusFilter, "all">;
  testAccount: boolean;
  tier: SubscriptionTier;
  userId: string;
}

export interface AdminUserDirectoryPage {
  items: AdminUserSummary[];
  page: number;
  pageCount: number;
  totalCount: number;
}

export interface AdminCreditHistoryEntry {
  amount: number;
  createdAt: string;
  feature: string;
  id: string;
  metadata: Json;
}

export interface AdminBanHistoryEntry {
  bannedAt: string;
  bannedBy: string;
  expiresAt: string | null;
  id: string;
  liftReason: string | null;
  liftedAt: string | null;
  liftedBy: string | null;
  reason: string;
}

export interface AdminUserPqrHistoryEntry {
  createdAt: string;
  id: string;
  status: string;
  subject: string;
  ticketRef: string;
}

export interface AdminUserDetail {
  activeBanExpiresAt: string | null;
  banHistory: AdminBanHistoryEntry[];
  billingStatus: string;
  creditBalance: number;
  creditTransactions: AdminCreditHistoryEntry[];
  deletedAt: string | null;
  deletionRequestedAt: string | null;
  deletionScheduledAt: string | null;
  email: string;
  firstName: string;
  isAdmin: boolean;
  languagePref: SupportedLocale;
  lastLoginAt: string | null;
  lastName: string;
  phone: string | null;
  pqrSubmissions: AdminUserPqrHistoryEntry[];
  registeredAt: string;
  testAccount: boolean;
  tier: SubscriptionTier;
  userId: string;
}

export interface CreateTestAccountInput {
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  tier: SubscriptionTier;
}

export interface CreateTestAccountResult {
  email: string;
  testAccount: boolean;
  tier: SubscriptionTier;
  userId: string;
}

export interface AdminCreditAdjustmentInput {
  amount: number;
  reason: string;
  userId: string;
}

export interface AdminCreditAdjustmentResult {
  feature: "admin_grant" | "admin_deduct";
  newBalance: number;
  userId: string;
}

export interface AdminTierUpdateInput {
  reason: string;
  tier: SubscriptionTier;
  userId: string;
}

export interface AdminTierUpdateResult {
  nextTier: SubscriptionTier;
  previousTier: SubscriptionTier;
  projectsLimit: number | null;
  projectsUsed: number | null;
  requiresProjectSelection: boolean;
  userId: string;
}

export interface BanUserInput {
  duration: AdminBanDuration;
  reason: string;
  userId: string;
}

export interface BanUserResult {
  expiresAt: string | null;
  status: "banned";
  userId: string;
}

export interface UnbanUserInput {
  reason: string;
  userId: string;
}

export interface UnbanUserResult {
  status: "active";
  userId: string;
}

export interface AdminDeleteAccountInput {
  reason: string;
  userId: string;
}

export interface AdminDeleteAccountResult {
  deletedAt: string;
  status: "deleted";
  userId: string;
}

export interface AdminPqrListFilters {
  category: "all" | AdminPqrCategory;
  page: number;
  searchText: string;
  status: "all" | AdminPqrStatus;
}

export interface AdminPqrListItem {
  category: AdminPqrCategory;
  createdAt: string;
  email: string;
  id: string;
  name: string;
  status: AdminPqrStatus;
  subject: string;
  ticketRef: string;
  userId: string | null;
}

export interface AdminPqrPage {
  items: AdminPqrListItem[];
  page: number;
  pageCount: number;
  totalCount: number;
}

export interface AdminPqrStatusHistoryEntry {
  changedAt: string;
  changedBy: string | null;
  note: string | null;
  status: AdminPqrStatus;
}

export interface AdminLogsFilters {
  endDate: string;
  page: number;
  searchText: string;
  severity: "all" | AdminLogSeverity;
  source: string;
  startDate: string;
}

export interface AdminAiAbuseReview {
  acknowledgedAt: string | null;
  clearedAt: string | null;
  detectedAt: string;
  id: string;
  notes: string | null;
  observed24hRequests: number;
  rateLimitFactor: number;
  status: AdminAiAbuseReviewStatus;
  tierAverage24hRequests: number;
  tierSnapshot: SubscriptionTier;
  usageMultiplier: number;
  userId: string;
}

export interface AdminAppLog {
  acknowledged: boolean;
  acknowledgedBy: string | null;
  alertStage?: string | null;
  createdAt: string;
  flaggedUserId?: string | null;
  id: string;
  message: string;
  metadata: Json;
  quotaObservedPercent?: number | null;
  quotaResourceKind?: AdminQuotaResourceKind | null;
  quotaThresholdPercent?: number | null;
  reviewStatus?: AdminAiAbuseReviewStatus | null;
  severity: AdminLogSeverity;
  source: string;
  usageMultiplier?: number | null;
}

export interface AdminLogsPage {
  items: AdminAppLog[];
  page: number;
  pageCount: number;
  totalCount: number;
}

export interface AcknowledgeAppLogResult {
  acknowledged: boolean;
  acknowledgedBy: string | null;
  logId: string;
}
